import * as React from 'react';
import {
  Home,
  Logout,
  Settings,
  Computer,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { LOGOUT, PRIVATE_ADMIN, PRIVATE_USER, CORPORATE_DOCUMENTS } from 'Shared/Routing/Application/Constants/PathsConstant';
import { APPLICATIONS_USER, PORTAL_USER } from 'User/Application/Constants/PathsConstant';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box, SvgIcon, Tooltip } from '@mui/material';

// Iconos estilo squircle blanco "gorditos" tal como se muestra en la referencia
const InstagramGorditoIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.8" stroke="currentColor" strokeWidth="2.3" fill="none" />
    <circle cx="12" cy="12" r="4.3" stroke="currentColor" strokeWidth="2.3" fill="none" />
    <circle cx="17" cy="7" r="1.3" fill="currentColor" />
  </SvgIcon>
);

const TikTokGorditoIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.8" stroke="currentColor" strokeWidth="2.3" fill="none" />
    <path
      fill="currentColor"
      d="M15.8 8.4a3.5 3.5 0 0 1-2.5-2.2h-2.1v9.2a2 2 0 1 1-2-2c.2 0 .4 0 .6.1v-2.1a4.1 4.1 0 1 0 3.5 4.1v-5.6a5.5 5.5 0 0 0 3-.1V8c-.2.2-.4.3-.5.4z"
    />
  </SvgIcon>
);

const LinkedInGorditoIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.8" stroke="currentColor" strokeWidth="2.3" fill="none" />
    <circle cx="7.5" cy="7.8" r="1.3" fill="currentColor" />
    <rect x="6.3" y="10.2" width="2.4" height="6.6" rx="0.5" fill="currentColor" />
    <path
      fill="currentColor"
      d="M10.8 10.2h2.2v1.1c.5-.7 1.3-1.3 2.5-1.3 2 0 2.9 1.2 2.9 3.2v3.6h-2.4v-3.2c0-.9-.3-1.5-1.3-1.5-1 0-1.5.7-1.5 1.7v3h-2.4v-6.6z"
    />
  </SvgIcon>
);

const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/aszendeoficial/',
    icon: InstagramGorditoIcon,
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@aszendeoficial',
    icon: TikTokGorditoIcon,
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/aszende-slu',
    icon: LinkedInGorditoIcon,
  },
];

export function MainListItems({ collapsed = false, isMobile = false, setMenuOpen }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdmin } = useAuthContext();

  const handleClick = () => {
    if (isMobile && setMenuOpen) {
      setMenuOpen(false);
    }
  };

  const selectedSx = {
    marginLeft: isMobile ? '5%' : (collapsed ? '0' : '10%'),
    marginBottom: '5px',
    width: isMobile ? '90%' : (collapsed ? '100%' : '90%'),
    color: 'primary.main',
    bgcolor: 'white',
    '&:hover': {
      bgcolor: 'primary.main',
      color: 'white',
    },
    borderRadius: isMobile ? '10px' : (collapsed ? '0' : '20px 0 0 20px'),
    justifyContent: collapsed ? 'center' : 'flex-start',
    '& .MuiListItemIcon-root': {
      minWidth: collapsed ? 0 : 40,
      justifyContent: 'center',
      margin: collapsed ? '0 auto' : '',
    },
  };

  const defaultSx = {
    marginLeft: isMobile ? '5%' : (collapsed ? '0' : '10%'),
    marginBottom: '5px',
    width: isMobile ? '90%' : (collapsed ? '100%' : '90%'),
    color: 'white',
    bgcolor: 'primary.main',
    '&:hover': {
      bgcolor: 'white',
      color: 'primary.main',
    },
    borderRadius: isMobile ? '10px' : (collapsed ? '0' : '20px 0 0 20px'),
    justifyContent: collapsed ? 'center' : 'flex-start',
    '& .MuiListItemIcon-root': {
      minWidth: collapsed ? 0 : 40,
      justifyContent: 'center',
      margin: collapsed ? '0 auto' : '',
    },
  };

  // Helper function to conditionally wrap with tooltip when collapsed
  const withTooltip = (component, title) => {
    return collapsed ? (
      <Tooltip title={title} placement="right">
        {component}
      </Tooltip>
    ) : component;
  };

  const listItems = [];

  // 1. Inicio
  listItems.push(
    withTooltip(
      <ListItemButton
        key={PRIVATE_USER}
        component={Link}
        to={PRIVATE_USER}
        sx={location.pathname === PRIVATE_USER ? selectedSx : defaultSx}
        onClick={handleClick}
      >
        <ListItemIcon>
          <Home />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.home', 'Inicio')} />}
      </ListItemButton>,
      t('ui.menus.user.home', 'Inicio')
    )
  );

  // 2. Empresa (nueva sección, de momento vacía, no hace nada al clicar)
  listItems.push(
    withTooltip(
      <ListItemButton
        key="menu-empresa"
        sx={{
          ...defaultSx,
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        <ListItemIcon>
          <BusinessIcon />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.company', 'Empresa')} />}
      </ListItemButton>,
      t('ui.menus.user.company', 'Empresa')
    )
  );

  // 3. Documentos
  const isDocumentsSelected = location.pathname.startsWith(CORPORATE_DOCUMENTS);
  listItems.push(
    withTooltip(
      <ListItemButton
        key={CORPORATE_DOCUMENTS}
        component={Link}
        to={CORPORATE_DOCUMENTS}
        sx={isDocumentsSelected ? selectedSx : defaultSx}
        onClick={handleClick}
      >
        <ListItemIcon>
          <DescriptionIcon />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.documents', 'Documentos')} />}
      </ListItemButton>,
      t('ui.menus.user.documents', 'Documentos')
    )
  );

  // 4. Portal (centraliza en cajitas agrupadas por funcionalidades el resto de elementos)
  const isPortalSelected = location.pathname.startsWith(PORTAL_USER);
  listItems.push(
    withTooltip(
      <ListItemButton
        key={PORTAL_USER}
        component={Link}
        to={PORTAL_USER}
        sx={isPortalSelected ? selectedSx : defaultSx}
        onClick={handleClick}
      >
        <ListItemIcon>
          <AppsIcon />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.portal', 'Portal')} />}
      </ListItemButton>,
      t('ui.menus.user.portal', 'Portal')
    )
  );

  // 5. Aplicaciones (la que tenemos actualmente)
  listItems.push(
    withTooltip(
      <ListItemButton
        key={APPLICATIONS_USER}
        component={Link}
        to={APPLICATIONS_USER}
        sx={location.pathname === APPLICATIONS_USER ? selectedSx : defaultSx}
        onClick={handleClick}
      >
        <ListItemIcon>
          <Computer />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.applications', 'Aplicaciones')} />}
      </ListItemButton>,
      t('ui.menus.user.applications', 'Aplicaciones')
    )
  );

  // 6. Administración (para quien tenga permiso)
  if (isAdmin()) {
    listItems.push(
      withTooltip(
        <ListItemButton
          key={PRIVATE_ADMIN}
          component={Link}
          to={PRIVATE_ADMIN}
          sx={location.pathname.startsWith(PRIVATE_ADMIN) ? selectedSx : defaultSx}
          onClick={handleClick}
        >
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          {!collapsed && <ListItemText primary={t('ui.menus.user.admin', 'Administración')} />}
        </ListItemButton>,
        t('ui.menus.user.admin', 'Administración')
      )
    );
  }

  // 7. Cerrar sesión
  listItems.push(
    withTooltip(
      <ListItemButton
        key={LOGOUT}
        component={Link}
        to={LOGOUT}
        sx={defaultSx}
        onClick={handleClick}
      >
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('ui.menus.user.logout', 'Cerrar sesión')} />}
      </ListItemButton>,
      t('ui.menus.user.logout', 'Cerrar sesión')
    )
  );

  // 8. Redes sociales de Aszende (Instagram, TikTok, LinkedIn) - perfectamente centradas al colapsar
  listItems.push(
    <Box
      key="social-links"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: collapsed ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: collapsed ? 1.5 : 2.5,
        mt: { xs: 4, sm: 5, md: 6 },
        mb: 2,
        px: collapsed ? 0 : 1,
        mx: 'auto',
      }}
    >
      {SOCIAL_LINKS.map((item) => {
        const IconComponent = item.icon;
        return (
          <Tooltip key={item.name} title={item.name} placement={collapsed ? 'right' : 'top'}>
            <Box
              component="a"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.name}
              sx={{
                width: collapsed ? '40px' : 'auto',
                height: collapsed ? '40px' : 'auto',
                margin: collapsed ? '0 auto' : 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '50%',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.15)',
                  opacity: 0.9,
                  bgcolor: collapsed ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: collapsed ? '24px' : '30px',
                },
              }}
            >
              <IconComponent />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );

  return (
    <React.Fragment>
      {listItems}
    </React.Fragment>
  );
}

MainListItems.propTypes = {
  collapsed: PropTypes.bool,
  isMobile: PropTypes.bool,
  setMenuOpen: PropTypes.func,
};
